import Co2OutlinedIcon from "@mui/icons-material/Co2Outlined";
import EcoOutlinedIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import RecyclingOutlinedIcon from "@mui/icons-material/RecyclingOutlined";
import ScaleOutlinedIcon from "@mui/icons-material/ScaleOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { Box } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { KpiGrid, KpiTile } from "../components/m3";
import { summarizeCircularityFlows } from "../functions/results/circularityResults";
import { KG_PER_TONNE, formatNumber, formatPercent } from "../functions/results/formatters";
import { ORG_PALETTE } from "../theme/style/organizationPalette";

const PERCENT_DIGITS = { maximumFractionDigits: 1 };

// Embodied carbon switches to tonnes once it reaches 1 t.
const formatCarbon = (valueKg) => {
  const kg = Number(valueKg) || 0;
  return kg >= KG_PER_TONNE
    ? { value: formatNumber(kg / KG_PER_TONNE), unit: "t CO₂e" }
    : { value: formatNumber(kg, { maximumFractionDigits: 0 }), unit: "kg CO₂e" };
};

/**
 * Headline circularity metrics for a product or project.
 *
 * @param {number} massKg - total material mass
 * @param {number} gwpKg - total embodied carbon (kg CO₂e)
 * @param {number} mciPercent - Material Circularity Indicator, 0-100
 * @param {number} pciPercent - Product / Project Circularity Indicator, 0-100
 * @param {Array} rows - per-material rows (combined_circularities) for the material flow tiles
 * @param {"Product"|"Project"} topic
 */
const CircularityKpiTiles = ({ massKg = 0, gwpKg = 0, mciPercent = 0, pciPercent = 0, rows = [], topic = "Product" }) => {
  const { t } = useTranslation();
  const carbon = formatCarbon(gwpKg);
  const flows = useMemo(() => summarizeCircularityFlows(rows), [rows]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: ORG_PALETTE.spacing.metricCardGap }}>
    <KpiGrid columns={4} dense>
      <KpiTile
        size="lg"
        label={t("Total Mass")}
        value={formatNumber(massKg)}
        unit="kg"
        icon={<ScaleOutlinedIcon />}
        tone="chain"
        footer={t("{{count}} materials", { count: rows.length })}
      />
      <KpiTile
        size="lg"
        label={t("Total Embodied Carbon")}
        value={carbon.value}
        unit={carbon.unit}
        icon={<Co2OutlinedIcon />}
        tone="gwp"
        footer={t("All materials")}
      />
      <KpiTile
        size="lg"
        label={t("Material Circularity Indicator")}
        value={formatNumber(mciPercent)}
        unit="%"
        icon={<RecyclingOutlinedIcon />}
        tone="cost"
        footer={t("Ellen MacArthur method")}
      />
      <KpiTile
        size="lg"
        label={t(`${topic} Circularity Indicator`)}
        value={formatNumber(pciPercent)}
        unit="%"
        icon={<AccountTreeOutlinedIcon />}
        tone="shield"
        footer={t("Adjusted for disassembly")}
      />
    </KpiGrid>
    <KpiGrid columns={3} dense>
      <KpiTile
        label={t("Virgin input")}
        value={formatNumber(flows.virginInputPercent, PERCENT_DIGITS)}
        unit="%"
        icon={<InventoryOutlinedIcon />}
      />
      <KpiTile
        label={t("Recovered at end of life")}
        value={formatNumber(flows.recoveryPercent, PERCENT_DIGITS)}
        unit="%"
        icon={<EcoOutlinedIcon />}
        tone="cost"
      />
      <KpiTile
        label={t("Waste to landfill")}
        value={formatNumber(flows.wasteOutputPercent, PERCENT_DIGITS)}
        unit="%"
        icon={<InventoryOutlinedIcon />}
      />
    </KpiGrid>
    </Box>
  );
};

export default CircularityKpiTiles;
